// Page: AdjustTags
// Purpose: Admin tool to review templates sequentially and adjust their tags,
// with pagination, jump-to, and success/error messaging. Requires admin access.
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/auth';
import { useToast } from '../../../components/ui/notifications/toast-notification.jsx';
import Footer from '../../../components/shared/Footer';
import { Tags, ArrowLeft, ChevronLeft, ChevronRight, Loader2, Search } from 'lucide-react';
import { LoadingSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';
import { getTemplatesForTagAdjustment, updateTemplateTags } from '../../../api/tags/tagsApi';
import { TAG_LIST } from '../../../data/tags/tagEnums';
import { isUserAdmin } from '../../../utils/admin/adminUtils';

export default function AdjustTags() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showError, setShowError] = useState(false);
  
  // Template navigation state
  const [templates, setTemplates] = useState([]);
  const [currentTemplateIndex, setCurrentTemplateIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  // Jump to template state
  const [jumpToNumber, setJumpToNumber] = useState('');
  const [isJumping, setIsJumping] = useState(false);
  
  // Tag selection state
  const [selectedTags, setSelectedTags] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Get the auth context
  const { user } = useAuth();
  const { addToast } = useToast();
  
  // Check if user is the admin
  const isAdmin = isUserAdmin(user);

  // Get current template
  const currentTemplate = templates[currentTemplateIndex];

  // Delay showing errors to prevent flashing
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setShowError(true);
      }, 500); 
      return () => clearTimeout(timer);
    } else {
      setShowError(false);
    }
  }, [error]);

  // Check if user is an admin, if not, redirect to home page
  useEffect(() => {
    if (user) {
      if (!isAdmin) {
        navigate('/app');
      } else {
        // Load initial templates
        loadTemplates(1);
      }
    } else if (user === null) {
      // No user, redirect to login
      navigate('/login');
    }
  }, [navigate, user, isAdmin]);

  // Update selected tags when current template changes
  useEffect(() => {
    if (currentTemplate) {
      setSelectedTags([...currentTemplate.tags]);
    }
  }, [currentTemplate]);

  // Load templates function
  const loadTemplates = async (page = 1, append = false) => {
    try {
      if (!append) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }
      
      const response = await getTemplatesForTagAdjustment(page, 25);
      
      if (append) {
        setTemplates(prev => [...prev, ...response.templates]);
      } else {
        setTemplates(response.templates);
        setCurrentTemplateIndex(0);
      }
      
      setCurrentPage(response.currentPage);
      setTotalCount(response.totalCount);
      setHasMore(response.hasMore);
      setError(null);
    } catch (err) {
      console.error('Error loading templates:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  // Handle jumping to specific template number
  const handleJumpToTemplate = async () => {
    const templateNumber = parseInt(jumpToNumber);
    
    if (!templateNumber || templateNumber < 1 || templateNumber > totalCount) {
      addToast(`Please enter a valid template number between 1 and ${totalCount}`, 'error');
      return;
    }

    try {
      setIsJumping(true);
      
      // Calculate which page this template is on (25 templates per page)
      const targetPage = Math.ceil(templateNumber / 25);
      const indexInPage = (templateNumber - 1) % 25;
      
      console.log(`Jumping to template ${templateNumber}: page ${targetPage}, index ${indexInPage}`);
      
      // Check if we need to load a different page
      if (targetPage !== currentPage) {
        // Load the target page
        await loadTemplates(targetPage);
        setCurrentTemplateIndex(indexInPage);
      } else {
        // Same page, just update index
        setCurrentTemplateIndex(indexInPage);
      }
      
      // Clear the input
      setJumpToNumber('');
      addToast(`Jumped to template ${templateNumber}`, 'success');
      
    } catch (err) {
      console.error('Error jumping to template:', err);
      addToast(`Failed to jump to template: ${err.message}`, 'error');
    } finally {
      setIsJumping(false);
    }
  };

  // Handle tag selection
  const handleTagToggle = (tag) => {
    setSelectedTags(prev => {
      if (prev.includes(tag)) {
        return prev.filter(t => t !== tag);
      } else {
        return [...prev, tag];
      }
    });
  };

  // Handle update template tags
  const handleUpdateTags = async () => {
    if (!currentTemplate) return;
    
    try {
      setIsUpdating(true);
      await updateTemplateTags(currentTemplate.id, selectedTags);
      
      // Update the template in our local state
      setTemplates(prev => prev.map(template => 
        template.id === currentTemplate.id 
          ? { ...template, tags: [...selectedTags] }
          : template
      ));
      
      addToast('Template tags updated successfully!', 'success');
      
      // Auto-advance to next template after successful update
      setTimeout(() => {
        handleContinue();
      }, 500); // Small delay to show the success message
      
    } catch (err) {
      console.error('Error updating template tags:', err);
      addToast(`Failed to update tags: ${err.message}`, 'error');
    } finally {
      setIsUpdating(false);
    }
  };

  // Handle continue to next template
  const handleContinue = () => {
    // Check if we need to load more templates
    if (currentTemplateIndex >= templates.length - 1) {
      if (hasMore) {
        // Load next batch and continue
        loadTemplates(currentPage + 1, true);
        setCurrentTemplateIndex(currentTemplateIndex + 1);
      } else {
        addToast('You have reached the end of all templates!', 'info');
      }
    } else {
      setCurrentTemplateIndex(currentTemplateIndex + 1);
    }
  };

  // Handle previous template
  const handlePrevious = () => {
    if (currentTemplateIndex > 0) {
      setCurrentTemplateIndex(currentTemplateIndex - 1);
    }
  };

  // Handle Enter key press in jump input
  const handleJumpInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleJumpToTemplate();
    }
  };

  if (isLoading && templates.length === 0) {
    return (
      <div className="p-6 flex items-center justify-center h-screen">
        <LoadingSpinner message="Loading templates for tag adjustment..." />
      </div>
    );
  }

  if (!isAdmin) {
    return null; // Redirect happens in useEffect
  }

  if (!currentTemplate) {
    return (
      <div className="p-6 w-full max-w-[1600px] mx-auto">
        <div className="mb-4">
          <button
            onClick={() => navigate('/app/private/admin')}
            className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            Back to Admin
          </button>
        </div>
        
        <div className="text-center py-12">
          <Tags className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No Templates Found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            There are no templates available for tag adjustment.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 w-full max-w-[1600px] mx-auto pb-20">
      {/* Back to Admin Dashboard button */}
      <div className="mb-4">
        <button
          onClick={() => navigate('/app/private/admin')}
          className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          Back to Admin
        </button>
      </div>

      {error && showError && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {error}
        </div>
      )}

      {/* Page header */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <Tags className="h-6 w-6 text-primary-600 dark:text-primary-400 mr-2" />
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Adjust Tags</h1>
        </div>
        <p className="text-gray-600 dark:text-gray-400">
          Review and adjust tags for each template to improve categorization and searchability.
        </p>
      </div>

      {/* Controls Bar */}
      <div className="mb-6 flex items-center justify-between bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        {/* Jump to Template */}
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-600 dark:text-gray-400">Jump to:</span>
          <div className="flex items-center space-x-1">
            <input
              type="number"
              min="1"
              max={totalCount}
              value={jumpToNumber}
              onChange={(e) => setJumpToNumber(e.target.value)}
              onKeyPress={handleJumpInputKeyPress}
              placeholder="Template #"
              className="w-24 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-900 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
              disabled={isJumping}
            />
            <button
              onClick={handleJumpToTemplate}
              disabled={isJumping || !jumpToNumber}
              className="inline-flex items-center px-2 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-900 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-800"
            >
              {isJumping ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Search className="w-3 h-3" />
              )}
            </button>
          </div>
        </div>

        {/* Template counter */}
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Template {currentTemplateIndex + 1} of {totalCount}
        </div>
      </div>

      {/* Template Display - Full Width */}
      <div className="bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-6 mb-6">
        {/* Template Image */}
        <div className="text-center mb-6">
          {currentTemplate.imageUrl ? (
            <img
              src={currentTemplate.imageUrl}
              alt={currentTemplate.title || currentTemplate.companyName}
              className="max-h-72 max-w-full mx-auto rounded-lg shadow-sm border border-gray-200 dark:border-gray-600"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          ) : (
            <div className="h-72 w-full max-w-lg mx-auto bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
              <Tags className="h-16 w-16 text-gray-400" />
            </div>
          )}
        </div>

        {/* Company Name */}
        <div className="text-center mb-8">
          <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
            {currentTemplate.companyName}
          </h3>
          {currentTemplate.title && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
              {currentTemplate.title}
            </p>
          )}
        </div>

        {/* Tag Selection - Full Width */}
        <div className="space-y-6">
          <h4 className="text-xl font-medium text-gray-900 dark:text-white">Select Tags</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3">
            {TAG_LIST.map((tag) => (
              <button
                key={tag}
                onClick={() => handleTagToggle(tag)}
                className={`px-4 py-3 text-sm font-medium rounded-lg border transition-colors ${
                  selectedTags.includes(tag)
                    ? 'bg-primary-100 border-primary-300 text-primary-800 dark:bg-primary-900/30 dark:border-primary-600 dark:text-primary-400'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
          
          {/* Selected tags summary */}
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-600">
            <p className="text-lg font-medium text-gray-900 dark:text-white mb-3">
              Selected tags ({selectedTags.length}):
            </p>
            <div className="flex flex-wrap gap-2">
              {selectedTags.length > 0 ? (
                selectedTags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-3 py-1 text-sm font-medium bg-primary-100 text-primary-800 rounded-lg dark:bg-primary-900/30 dark:text-primary-400"
                  >
                    {tag}
                  </span>
                ))
              ) : (
                <span className="text-lg text-gray-500 dark:text-gray-400 italic">
                  No tags selected
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation and Action Buttons */}
      <div className="flex items-center justify-between">
        {/* Previous Button */}
        <button
          onClick={handlePrevious}
          disabled={currentTemplateIndex === 0}
          className="inline-flex items-center px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-900 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-800"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Previous
        </button>

        {/* Action Buttons */}
        <div className="flex items-center space-x-4">
          {/* Continue Button */}
          <button
            onClick={handleContinue}
            disabled={isLoadingMore}
            className="inline-flex items-center px-8 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:bg-gray-900 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-800"
          >
            {isLoadingMore ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Loading...
              </>
            ) : (
              <>
                Continue
                <ChevronRight className="w-4 h-4 ml-1" />
              </>
            )}
          </button>

          {/* Update Button */}
          <button
            onClick={handleUpdateTags}
            disabled={isUpdating}
            className="inline-flex items-center px-8 py-3 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-primary-500 dark:hover:bg-primary-600"
          >
            {isUpdating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Updating...
              </>
            ) : (
              'Update'
            )}
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
} 